
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import Step6 from './Step6';
import { btnStyle, wizardContainerStyle } from '../styles';

/**
 * The six-step wizard for recording a new collection.
 *
 * It owns nothing. Every piece of state is the caller's, which is what lets
 * the same wizard be opened fresh or resumed from a saved draft without
 * knowing the difference.
 *
 * Two rules live here rather than in the steps. Next is refused unless the
 * current step is answered -- see collections/wizardRules.js -- because a
 * record missing what only the person standing over the specimen could supply
 * cannot be completed later. And Save as draft is offered from every step, on
 * the single condition that the record has a code: somebody in a field with a
 * dying battery should always be able to keep what they have.
 */
export default function CollectionWizard({
  t, user, canEdit,
  step, setStep, onClose, canAdvance,
  form, onFormChange, setForm, vegetation,
  regionField, vegField, taxonField, photoField, suppField,
  draftSaved, error, setError, saving, onSubmit,
}) {
  const advance = () => {
    if (canAdvance(step)) { setError(''); setStep(s => s + 1); }
  };
  const goBack = () => {
    setError('');
    if (step === 1) onClose();
    else setStep(s => s - 1);
  };

  return (
    <div style={wizardContainerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>{t('wizard.title')}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('wizard.stepOf', { step: step })}</span>
          <button onClick={onClose} title={t('tip.closeWizard')} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)' }}>×</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 5, marginBottom: 20 }}>
        {[1, 2, 3, 4, 5, 6].map(s => (
          <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= step ? 'var(--c-primary)' : 'var(--c-border)' }} />
        ))}
      </div>

      {draftSaved && (
        <div style={{ background: 'var(--c-primary-soft)', border: '1px solid var(--c-primary-border-2)', borderRadius: 6, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--c-success-2-fg)' }}>
          <span>{t('wizard.draftSavedBanner')}</span>
          <button type="button" onClick={onClose} title={t('tip.closeWizardSaved')}
            style={{ background: 'var(--c-success-5)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, marginInlineStart: 12, whiteSpace: 'nowrap' }}>
            {t('wizard.closeWizard')}
          </button>
        </div>
      )}
      {error && <p style={{ color: 'red', margin: '0 0 14px', fontSize: 13 }}>{error}</p>}

      {step === 1 && <Step1 t={t} user={user} form={form} setForm={setForm} />}
      {step === 2 && (
        <Step2 t={t} canEdit={canEdit} form={form} onFormChange={onFormChange}
          setForm={setForm} regionField={regionField} vegField={vegField}
          supplementary={suppField.list} setSupplementary={suppField.setList}
          supplementaryInput={suppField.input}
          setSupplementaryInput={suppField.setInput} />
      )}
      {step === 3 && <Step3 t={t} form={form} onFormChange={onFormChange} />}
      {step === 4 && <Step4 t={t} user={user} form={form} onFormChange={onFormChange}
               onRankChange={taxonField.onRankChange} taxon={taxonField.box}
               taxonSelected={taxonField.selected} onTaxonPick={taxonField.onPick}
               onMarkUnknown={taxonField.onMarkUnknown} />}
      {step === 5 && <Step5 t={t} form={form} vegetation={vegetation} photos={photoField.slots}
               setPhoto={photoField.setPhoto} existingPhotoTypes={photoField.existingTypes}
               extraPhotos={photoField.extra} setExtraPhotos={photoField.setExtra}
               otherPhotoNote={photoField.otherNote} setOtherPhotoNote={photoField.setOtherNote}
               vegUnknownPhoto={photoField.vegUnknown} setVegUnknownPhoto={photoField.setVegUnknown}
               setColourChange={photoField.setColourChange} />}
      {step === 6 && <Step6 t={t} form={form} onFormChange={onFormChange}
               vegetation={vegetation} photos={photoField.slots} extraPhotos={photoField.extra}
               vegUnknownPhoto={photoField.vegUnknown}
               audioNotes={photoField.audio} setAudioNotes={photoField.setAudio} saving={saving} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 22 }}>
        <button type="button" onClick={goBack} title={t('tip.wizardBack')} style={btnStyle('var(--c-text-muted)')}>
          {step === 1 ? t('wizard.cancel') : t('wizard.back')}
        </button>
        {step < 6 ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div title={!form.qr_uid.trim() ? t('wizard.saveDraftTitle') : undefined}
              style={{ display: 'inline-block' }}>
              <button type="button" data-testid="wizard-save-draft" title={t('tip.saveDraft')}
                disabled={!form.qr_uid.trim() || saving}
                onClick={() => onSubmit(true)}
                style={btnStyle(!form.qr_uid.trim() ? 'var(--c-border-strong)' : 'var(--c-text-muted)')}>
                {saving ? t('common:saving') : t('wizard.saveDraft')}
              </button>
            </div>
            <button type="button" onClick={advance} data-testid="wizard-next" title={t('tip.next')}
              disabled={!canAdvance(step)}
              style={btnStyle(canAdvance(step) ? 'var(--c-primary)' : 'var(--c-text-faint)')}>
              {t('wizard.next')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" disabled={saving} onClick={() => onSubmit(true)} title={t('tip.saveAsDraft')} style={btnStyle('var(--c-text-muted)')}>
              {saving ? t('common:saving') : t('wizard.saveAsDraft')}
            </button>
            <button type="button" disabled={saving} onClick={() => onSubmit(false)} title={t('tip.submit')} style={btnStyle('var(--c-primary)')}>
              {saving ? t('common:saving') : t('wizard.submit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
